import { useBulkUpdateStudentsMutation } from '@/app/redux/endpoints/students';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { resetLevelProgress } from '@/common/utils/helpers';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Label } from '@shadcn/components/label';
import { chunk } from 'lodash';
import { useState } from 'react';
import { toast } from 'sonner';
import BulkLevelSelect from './BulkLevelSelect';

interface BulkLevelChangeDialogProps {
  open: boolean;
  onClose: () => void;
  students: Student[];
}
// BulkLevelChangeDialog.tsx
export default function BulkLevelChangeDialog({
  open,
  onClose,
  students,
}: BulkLevelChangeDialogProps) {
  const [bulkUpdateStudents] = useBulkUpdateStudentsMutation();

  const { currentOrganisationId } = useCurrentOrganisationId();

  const initialLevel = students.length > 0 ? students[0].level : 1;

  const [selectedLevel, setSelectedLevel] = useState<number>(initialLevel);
  const [resetProgress, setResetProgress] = useState(false);

  async function handleSave() {
    if (!currentOrganisationId) {
      console.error('Missing org ID');
      toast.error('Failed to update levels', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const allUpdates = students.map((student) => ({
      studentId: student.id,
      level: selectedLevel,
      levelsProgress: resetProgress
        ? resetLevelProgress(selectedLevel, student.levelsProgress)
        : undefined,
    }));
    const chunks = chunk(allUpdates, 50);
    let updated = 0;

    onClose();
    const toastId = toast.loading('Updating levels...');

    try {
      for (const c of chunks) {
        await bulkUpdateStudents({
          organisationId: currentOrganisationId,
          updates: c,
        }).unwrap();
        updated += c.length;
        toast.loading(
          `Updating levels (${updated}/${allUpdates.length})`,
          { id: toastId },
        );
      }
      toast.success('Learner levels updated', { id: toastId, description: '' });
    } catch (error) {
      console.error('Failed to update levels', error);
      if (updated > 0) {
        toast.error('Failed to update levels', {
          id: toastId,
          description: `Updated ${updated} of ${allUpdates.length}. Please retry.`,
        });
      } else {
        toast.error('Failed to update levels', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    }
  }

  return students.length ? (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Change Level for{' '}
            {students[0].studentFirstName + ' ' + students[0].studentSurname}{' '}
            {students.length > 1
              ? `+ ${students.length - 1} other${
                  students.length - 1 === 1 ? '' : 's'
                }`
              : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col">
          <label className="text-sm font-medium">Select Level</label>
          <div className="py-1">
            <BulkLevelSelect
              onLevelChange={setSelectedLevel}
              initialLevel={initialLevel}
            />
          </div>

          <div className="flex items-center mt-4 gap-2">
            <Checkbox
              id="reset-progress"
              checked={resetProgress}
              onCheckedChange={(checked) =>
                setResetProgress(checked === 'indeterminate' ? false : checked)
              }
            />
            <Label htmlFor="reset-progress">
              Reset progress for this level
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;
}
